import React, { lazy, Suspense } from "react";
import "./App.css";
import "./styles/admin-themes.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./context/LanguageContext";
import { HeroMusicProvider } from "./context/HeroMusicContext";
import { SiteLayoutProvider } from "./context/SiteLayoutContext";
import { AnalyticsTracker } from "./components/AnalyticsTracker";
import { AuthProvider } from "./context/AuthContext";
import { ClientAuthProvider } from "./context/ClientAuthContext";
import ForcePasswordChangeModal from "./components/ForcePasswordChangeModal";
import { ClientPreferencesProvider } from "./context/ClientPreferencesContext";
import ClientA11yButton from "./components/ClientA11yButton";
import ClientWhatsAppFAB from "./components/ClientWhatsAppFAB";
import { OfflineBanner } from "./components/OfflineBanner";
import { useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import ClientNav from "./components/ClientNav";
import Footer from "./components/Footer";
import { Loader2 } from "lucide-react";
import { Toaster } from "./components/ui/toaster";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import ScrollToTop from "./components/ScrollToTop";
import RequirePermission from "./components/admin/RequirePermission";

// Suspense fallback used while a lazy chunk is being downloaded.
const RouteFallback = () => (
  <div className="flex items-center justify-center h-screen" data-testid="route-loading">
    <Loader2 className="w-8 h-8 animate-spin text-demarre-600" />
  </div>
);

// Public pages — eager only for first paint (Home/Navbar/Footer)
import Home from "./pages/Home";
// All other public pages → lazy chunks
const Pilgrimages = lazy(() => import("./pages/Pilgrimages"));
const LeadAGroup = lazy(() => import("./pages/LeadAGroup"));
const InteractiveMap = lazy(() => import("./pages/InteractiveMap"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const TourDetail = lazy(() => import("./pages/TourDetail"));
const Booking = lazy(() => import("./pages/Booking"));
const HoldDetails = lazy(() => import("./pages/HoldDetails"));
const Payment = lazy(() => import("./pages/Payment"));
const ClientPayment = lazy(() => import("./pages/ClientPayment"));
const ClientAuth = lazy(() => import("./pages/ClientAuth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const MyBookings = lazy(() => import("./pages/MyBookings"));
const MyDocuments = lazy(() => import("./pages/MyDocuments"));
// GuidePanel exports GuideLogin + GuideDashboard — wrap in named->default for React.lazy
const GuideLogin = lazy(() => import("./pages/GuidePanel").then((m) => ({ default: m.GuideLogin })));
const GuideDashboard = lazy(() => import("./pages/GuidePanel").then((m) => ({ default: m.GuideDashboard })));
const GuideToilets = lazy(() => import("./pages/GuideToilets"));
const AutomationToggles = lazy(() => import("./pages/admin/AutomationToggles"));
const EmailStudioEditor = lazy(() => import("./pages/admin/EmailStudioEditor"));

// Heavy public pages → lazy
const TripCommunity = lazy(() => import("./pages/TripCommunity"));
const TravelTips = lazy(() => import("./pages/TravelTips"));
const TourComparator = lazy(() => import("./pages/TourComparator"));
const GamesHub = lazy(() => import("./pages/TriviaQuiz"));
const ClientProfile = lazy(() => import("./pages/ClientProfile"));
const CurrencyConverter = lazy(() => import("./pages/CurrencyConverter"));
const TimeZones = lazy(() => import("./pages/TimeZones"));
const Weather = lazy(() => import("./pages/Weather"));
const SpiritualLife = lazy(() => import("./pages/SpiritualLife"));
const Bible = lazy(() => import("./pages/Bible"));
const DailyReadings = lazy(() => import("./pages/DailyReadings"));
const Rosary = lazy(() => import("./pages/Rosary"));
const TestimonialSubmit = lazy(() => import("./pages/TestimonialSubmit"));
const QuoteAcceptPage = lazy(() => import("./pages/QuoteAcceptPage"));
const ClientTestimonials = lazy(() => import("./pages/ClientTestimonials"));
const ContractSign = lazy(() => import("./pages/ContractSign"));
const ReviewSubmit = lazy(() => import("./pages/ReviewSubmit"));
const PublicMeetingCheckin = lazy(() => import("./pages/PublicMeetingCheckin"));
const PublicReceipt = lazy(() => import("./pages/PublicReceipt"));
const EmbaixadorLogin = lazy(() => import("./pages/EmbaixadorLogin"));
const EmbaixadorAtivar = lazy(() => import("./pages/EmbaixadorAtivar"));
const EmbaixadorEsqueci = lazy(() => import("./pages/EmbaixadorEsqueci"));
const EmbaixadorRedefinir = lazy(() => import("./pages/EmbaixadorRedefinir"));
const EmbaixadorDashboard = lazy(() => import("./pages/EmbaixadorDashboard"));
const EmbaixadorClienteDetail = lazy(() => import("./pages/EmbaixadorClienteDetail"));
const EmbaixadorTourDetail = lazy(() => import("./pages/EmbaixadorTourDetail"));
const EmbaixadorFinanceiro = lazy(() => import("./pages/EmbaixadorFinanceiro"));
const EmbaixadorPerfil = lazy(() => import("./pages/EmbaixadorPerfil"));
const EmbaixadorAcademia = lazy(() => import("./pages/EmbaixadorAcademia"));
const EmbaixadorAcademiaCategoria = lazy(() => import("./pages/EmbaixadorAcademiaCategoria"));
const EmbaixadorAcademiaConteudo = lazy(() => import("./pages/EmbaixadorAcademiaConteudo"));
const EmbaixadorAcademiaBusca = lazy(() => import("./pages/EmbaixadorAcademiaBusca"));
const EmbaixadorAcademiaFavoritos = lazy(() => import("./pages/EmbaixadorAcademiaFavoritos"));
const EmbaixadorMateriais = lazy(() => import("./pages/EmbaixadorMateriais"));
const EmbaixadorCalendario = lazy(() => import("./pages/EmbaixadorCalendario"));
const EmbaixadorLembretes = lazy(() => import("./pages/EmbaixadorLembretes"));
const AmbassadorsManager = lazy(() => import("./pages/admin/AmbassadorsManager"));
const AmbassadorDetail = lazy(() => import("./pages/admin/AmbassadorDetail"));
const AdminAmbassadorLeads = lazy(() => import("./pages/admin/AdminAmbassadorLeads"));
const MaterialsManager = lazy(() => import("./pages/admin/MaterialsManager"));
const EmbaixadorSSO = lazy(() => import("./pages/EmbaixadorSSO"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin pages → all lazy (entire admin bundle deferred until /admin/* is hit)
const AdminLogin = lazy(() => import("./pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const ToursList = lazy(() => import("./pages/admin/ToursList"));
const TourForm = lazy(() => import("./pages/admin/TourForm"));
const BookingsList = lazy(() => import("./pages/admin/BookingsList"));
const FAQsManager = lazy(() => import("./pages/admin/FAQsManager"));
const FAQs = lazy(() => import("./pages/FAQs"));
const EmailComposer = lazy(() => import("./pages/admin/EmailComposer"));
const ClientManager = lazy(() => import("./pages/admin/ClientManager"));
const GalleryManager = lazy(() => import("./pages/admin/GalleryManager"));
const RoomingList = lazy(() => import("./pages/admin/RoomingList"));
const ClientDetail = lazy(() => import("./pages/admin/ClientDetail"));
const PaymentSettings = lazy(() => import("./pages/admin/PaymentSettings"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));
const BookingManager = lazy(() => import("./pages/admin/BookingManager"));
const Reports = lazy(() => import("./pages/admin/Reports"));
const SiteAnalytics = lazy(() => import("./pages/admin/SiteAnalytics"));
const AdminHealth = lazy(() => import("./pages/admin/AdminHealth"));
const SalesPreviewPublic = lazy(() => import("./pages/SalesPreviewPublic"));
const AtelierBuilder = lazy(() => import("./pages/admin/AtelierBuilder"));
const SpotlightsManager = lazy(() => import("./pages/admin/SpotlightsManager"));
const AtelierDraftsHistory = lazy(() => import("./pages/admin/AtelierDraftsHistory"));
const AtelierMaintenance = lazy(() => import("./pages/admin/AtelierMaintenance"));
const EmailContracts = lazy(() => import("./pages/admin/EmailContracts"));
const HotelManager = lazy(() => import("./pages/admin/HotelManager"));
const QuizManager = lazy(() => import("./pages/admin/QuizManager"));
const MemoryCardManager = lazy(() => import("./pages/admin/MemoryCardManager"));
const BackupManager = lazy(() => import("./pages/admin/BackupManager"));
const UserManager = lazy(() => import("./pages/admin/UserManager"));
const AuditLog = lazy(() => import("./pages/admin/AuditLog"));
const CommunityAudit = lazy(() => import("./pages/admin/CommunityAudit"));
const ClientActivityLog = lazy(() => import("./pages/admin/ClientActivityLog"));
const SystemAuditsLayout = lazy(() => import("./pages/admin/SystemAuditsLayout"));
const TestimonialsManager = lazy(() => import("./pages/admin/TestimonialsManager"));
const NotificationTemplates = lazy(() => import("./pages/admin/NotificationTemplates"));
const ExchangeRateManager = lazy(() => import("./pages/admin/ExchangeRateManager"));
const GoalsSettings = lazy(() => import("./pages/admin/GoalsSettings"));
const AdminClientContent = lazy(() => import("./pages/admin/AdminClientContent"));
const SalesHistoryPage = lazy(() => import("./pages/admin/SalesHistoryPage"));
const Pipeline = lazy(() => import("./pages/admin/Pipeline"));
const GuideManager = lazy(() => import("./pages/admin/GuideManager"));
const ContractDashboard = lazy(() => import("./pages/admin/ContractDashboard"));
const RemindersPage = lazy(() => import("./pages/admin/RemindersPage"));
const WaitlistManager = lazy(() => import("./pages/admin/WaitlistManager"));
const ReviewsManager = lazy(() => import("./pages/admin/ReviewsManager"));
const TourMaterials = lazy(() => import("./pages/admin/TourMaterials"));
const MeetingCheckin = lazy(() => import("./pages/admin/MeetingCheckin"));
const TourHotels = lazy(() => import("./pages/admin/TourHotels"));
const CitiesLibrary = lazy(() => import("./pages/admin/CitiesLibrary"));
const AdminHomepageSettings = lazy(() => import("./pages/admin/AdminHomepageSettings"));
const AdminForgotPassword = lazy(() => import("./pages/admin/AdminForgotPassword"));
const AdminResetPassword = lazy(() => import("./pages/admin/AdminResetPassword"));
const EmailSignature = lazy(() => import("./pages/admin/EmailSignature"));
const AdminCommunities = lazy(() => import("./pages/admin/AdminCommunities"));
const HoldSettings = lazy(() => import("./pages/admin/HoldSettings"));
const BookingConflicts = lazy(() => import("./pages/admin/BookingConflicts"));
const ItineraryBuilder = lazy(() => import("./pages/admin/ItineraryBuilder"));
const PdfTemplateBuilder = lazy(() => import("./pages/admin/PdfTemplateBuilder"));
const BadgeGenerator = lazy(() => import("./pages/admin/BadgeGenerator"));
const BirthdayCards = lazy(() => import("./pages/admin/BirthdayCards"));
const TourExpenses = lazy(() => import("./pages/admin/TourExpenses"));
const SupplierManager = lazy(() => import("./pages/admin/SupplierManager"));
const AdminCalendar = lazy(() => import("./pages/admin/AdminCalendar"));
const HelpAnalytics = lazy(() => import("./pages/admin/HelpAnalytics"));
const AcademyContentEditor = lazy(() => import("./pages/admin/AcademyContentEditor"));

// Design preview mockups — removed in iter 372 (Feb/2026) to free disk space.
// If you need them again, they live in git history (/app/frontend/src/pages/designPreview/).

// Iter 320av + 320aw — Global overlays for pilgrim-facing routes:
//   • OfflineBanner: top-fixed banner reflecting online/offline/syncing state.
//   • ClientA11yButton: bottom-fixed FAB for font-size + high-contrast.
// Never mounted on admin pages. Consolidates the previously per-page
// OfflineBanner mounts so newer client pages get the banner automatically.
const ClientA11yGate = () => {
  const { pathname } = useLocation();
  if (pathname.startsWith('/admin')) return null;
  if (pathname === '/login' || pathname.startsWith('/forgot-password')) return null;
  return (
    <>
      <OfflineBanner />
      <ClientA11yButton />
      <ClientWhatsAppFAB />
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <ClientAuthProvider>
        <ClientPreferencesProvider>
        <LanguageProvider>
          <HeroMusicProvider>
          <SiteLayoutProvider>
          <BrowserRouter>
            <ScrollToTop />
            <AnalyticsTracker />
            <div className="App">
              <ClientA11yGate />
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                {/* Admin routes */}
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
                <Route path="/admin/reset-password" element={<AdminResetPassword />} />
                <Route path="/admin" element={<AdminLayout />}>
                  <Route path="dashboard" element={<Dashboard />} />
                  <Route path="tours" element={<ToursList />} />
                  <Route path="tours/new" element={<TourForm />} />
                  <Route path="tours/edit/:id" element={<TourForm />} />
                  <Route path="tours/:tourId/rooming-list" element={<RoomingList />} />
                  <Route path="tours/:tourId/materials" element={<TourMaterials />} />
                  <Route path="tours/:tourId/meeting" element={<MeetingCheckin />} />
                  <Route path="tours/:tourId/hotels" element={<TourHotels />} />
                  <Route path="tours/:tourId/itinerary" element={<ItineraryBuilder />} />
                  <Route path="tours/:tourId/pdf-template" element={<PdfTemplateBuilder />} />
                  <Route path="tours/:tourId/badges" element={<BadgeGenerator />} />
                  <Route path="tours/:tourId/birthday-cards" element={<BirthdayCards />} />
                  <Route path="tours/:tourId/expenses" element={<TourExpenses />} />
                  <Route path="suppliers" element={<SupplierManager />} />
                  <Route path="calendar" element={<AdminCalendar />} />
                  <Route path="help-analytics" element={<HelpAnalytics />} />
                  <Route path="academy" element={<Navigate to="/admin/ambassadors?tab=academia" replace />} />
                  <Route path="academy/content/new" element={<RequirePermission perm="ambassadors"><AcademyContentEditor /></RequirePermission>} />
                  <Route path="academy/content/:id" element={<RequirePermission perm="ambassadors"><AcademyContentEditor /></RequirePermission>} />
                  <Route path="bookings" element={<BookingManager />} />
                  <Route path="sales-history" element={<SalesHistoryPage />} />
                  <Route path="pipeline" element={<Pipeline />} />
                  <Route path="payments" element={<AdminPayments />} />
                  <Route path="clients" element={<ClientManager />} />
                  <Route path="clients/:clientId" element={<ClientDetail />} />
                  <Route path="emails" element={<EmailComposer />} />
                  <Route path="gallery" element={<GalleryManager />} />
                  <Route path="aparencia" element={<AdminHomepageSettings />} />
                  <Route path="homepage" element={<Navigate to="/admin/aparencia" replace />} />
                  <Route path="payment-settings" element={<PaymentSettings />} />
                  <Route path="reports" element={<Reports />} />
                  <Route path="analytics" element={<SiteAnalytics />} />
                  <Route path="health" element={<Navigate to="/admin/system-audits/health" replace />} />
                  <Route path="atelier" element={<AtelierBuilder />} />
                  <Route path="spotlights" element={<SpotlightsManager />} />
                  <Route path="atelier/historico" element={<AtelierDraftsHistory />} />
                  <Route path="atelier/manutencao" element={<AtelierMaintenance />} />
                  <Route path="atelier/:draftId" element={<AtelierBuilder />} />
                  <Route path="email-contracts" element={<EmailContracts />} />
                  <Route path="faqs" element={<FAQsManager />} />
                  <Route path="hotels" element={<HotelManager />} />
                  <Route path="cities" element={<CitiesLibrary />} />
                  <Route path="backup" element={<Navigate to="/admin/system-audits/backup" replace />} />
                  <Route path="users" element={<UserManager />} />
                  <Route path="audit-log" element={<Navigate to="/admin/system-audits/audit-log" replace />} />
                  <Route path="community-audit" element={<Navigate to="/admin/system-audits/community-audit" replace />} />
                  <Route path="client-activity" element={<Navigate to="/admin/system-audits/client-activity" replace />} />

                  {/* Iter 285 — Auditorias do Sistema (sub-abas consolidadas) */}
                  <Route path="system-audits" element={<SystemAuditsLayout />}>
                    <Route index element={<Navigate to="client-activity" replace />} />
                    <Route path="client-activity" element={<ClientActivityLog />} />
                    <Route path="community-audit" element={<CommunityAudit />} />
                    <Route path="backup" element={<BackupManager />} />
                    <Route path="audit-log" element={<AuditLog />} />
                    <Route path="health" element={<AdminHealth />} />
                  </Route>
                  <Route path="testimonials" element={<TestimonialsManager />} />
                  <Route path="notifications" element={<NotificationTemplates />} />
                  <Route path="cotacoes" element={<ExchangeRateManager />} />
                  <Route path="quiz" element={<QuizManager />} />
                  <Route path="memory-cards" element={<MemoryCardManager />} />
                  <Route path="goals" element={<GoalsSettings />} />
                  <Route path="client-content" element={<AdminClientContent />} />
                  <Route path="guides" element={<GuideManager />} />
                  <Route path="ambassadors" element={<RequirePermission perm="ambassadors"><AmbassadorsManager /></RequirePermission>} />
                  {/* Legacy direct link — auto-opens the "leads" tab */}
                  <Route path="ambassador-leads" element={<RequirePermission perm="ambassadors"><AmbassadorsManager /></RequirePermission>} />
                  <Route path="materials" element={<MaterialsManager />} />
                  <Route path="ambassadors/:id" element={<AmbassadorDetail />} />
                  <Route path="contracts-dashboard" element={<ContractDashboard />} />
                  <Route path="lembretes" element={<RemindersPage />} />
                  <Route path="waitlist" element={<WaitlistManager />} />
                  <Route path="reviews" element={<ReviewsManager />} />
                  <Route path="email-signature" element={<EmailSignature />} />
                  <Route path="communities" element={<AdminCommunities />} />
                  <Route path="hold-settings" element={<HoldSettings />} />
                  {/* Legacy redirect: /admin/booking-conflicts → /admin/hold-settings (Tab Conflitos) */}
                  <Route path="booking-conflicts" element={<BookingConflicts />} />
                  {/* Admin community view = same UI as client (TripCommunity).
                      Backend marks admin role on /preview so write actions stay enabled. */}
                  <Route path="communities/:tourId" element={<TripCommunity />} />
                  {/* community-testimonials now handled within TestimonialsManager tabs */}
                  <Route path="tours/:tourId/community" element={<TripCommunity />} />
                  <Route path="toilets" element={<GuideToilets />} />
                  <Route path="automations" element={<AutomationToggles />} />
                  <Route path="automations/studio/:key" element={<EmailStudioEditor />} />
                  <Route path="*" element={<NotFound />} />
                </Route>

                {/* Guide routes (standalone, no navbar/footer) */}
                <Route path="/guia/*" element={
                  <Routes>
                    <Route index element={<GuideLogin />} />
                    <Route path="login" element={<GuideLogin />} />
                    <Route path="dashboard" element={<GuideDashboard />} />
                    <Route path="toilets" element={<GuideToilets />} />
                  </Routes>
                } />

                {/* Contract signing (standalone page, no navbar/footer) */}
                <Route path="/contract/sign/:token" element={<ContractSign />} />
                <Route path="/avaliacao/:token" element={<ReviewSubmit />} />
                <Route path="/testimonial/:token" element={<TestimonialSubmit />} />
                <Route path="/receipt/:token" element={<PublicReceipt />} />
                <Route path="/preview/:token" element={<SalesPreviewPublic />} />
                <Route path="/quote/:token" element={<QuoteAcceptPage />} />

                {/* Design preview mockups (standalone, no global navbar/footer) */}
                <Route path="/design-preview/*" element={<Navigate to="/" replace />} />

                {/* Embaixadores (Ambassadors) area — standalone, no public Navbar/Footer */}
                <Route path="/embaixador/login" element={<EmbaixadorLogin />} />
                <Route path="/embaixador/sso" element={<EmbaixadorSSO />} />
                <Route path="/embaixador/ativar" element={<EmbaixadorAtivar />} />
                <Route path="/embaixador/esqueci" element={<EmbaixadorEsqueci />} />
                <Route path="/embaixador/redefinir" element={<EmbaixadorRedefinir />} />
                <Route path="/embaixador" element={<EmbaixadorDashboard />} />
                <Route path="/embaixador/dashboard" element={<EmbaixadorDashboard />} />
                <Route path="/embaixador/clientes/:id" element={<EmbaixadorClienteDetail />} />
                <Route path="/embaixador/peregrinacoes/:id" element={<EmbaixadorTourDetail />} />
                <Route path="/embaixador/financeiro" element={<EmbaixadorFinanceiro />} />
                <Route path="/embaixador/perfil" element={<EmbaixadorPerfil />} />
                {/* Academia Demarré — nova home + aliases para preservar links antigos */}
                <Route path="/embaixador/academia" element={<EmbaixadorAcademia />} />
                <Route path="/embaixador/academia/busca" element={<EmbaixadorAcademiaBusca />} />
                <Route path="/embaixador/academia/favoritos" element={<EmbaixadorAcademiaFavoritos />} />
                <Route path="/embaixador/academia/categoria/:slug" element={<EmbaixadorAcademiaCategoria />} />
                <Route path="/embaixador/academia/conteudo/:id" element={<EmbaixadorAcademiaConteudo />} />
                {/* Legacy alias — redireciona para a nova Academia (preserva links antigos) */}
                <Route path="/embaixador/treinamentos" element={<Navigate to="/embaixador/academia" replace />} />
                <Route path="/embaixador/materiais" element={<EmbaixadorMateriais />} />
                <Route path="/embaixador/calendario" element={<EmbaixadorCalendario />} />
                <Route path="/embaixador/lembretes" element={<EmbaixadorLembretes />} />

                {/* Public routes */}
                <Route path="*" element={
                  <>
                    <Navbar />
                    <main className="pt-[150px] sm:pt-[135px]">
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/pilgrimages" element={<Pilgrimages />} />
                        <Route path="/lead-a-group" element={<LeadAGroup />} />
                        <Route path="/mapa" element={<InteractiveMap />} />
                        <Route path="/map" element={<InteractiveMap />} />
                        <Route path="/galeria" element={<GalleryPage />} />
                        <Route path="/comparar" element={<TourComparator />} />
                        <Route path="/reuniao/:tourId" element={<PublicMeetingCheckin />} />
                        <Route path="/tour/:id" element={<TourDetail />} />
                        <Route path="/booking/:id" element={<Booking />} />
                        <Route path="/hold/:token" element={<HoldDetails />} />
                        <Route path="/payment/:bookingId" element={<Payment />} />
                        <Route path="/login" element={<ClientAuth />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        {/* Client area pages with ClientNav */}
                        <Route path="/meu-perfil" element={<><ClientNav /><div className="pt-0 sm:pt-14 pb-24 sm:pb-0"><ClientProfile /></div></>} />
                        <Route path="/minhas-viagens" element={<><ClientNav /><div className="pt-0 sm:pt-14 pb-24 sm:pb-0"><MyBookings /></div></>} />
                        <Route path="/my-bookings" element={<><ClientNav /><div className="pt-0 sm:pt-14 pb-24 sm:pb-0"><MyBookings /></div></>} />
                        <Route path="/my-bookings/pay/:bookingId" element={<><ClientNav /><div className="pt-0 sm:pt-14 pb-24 sm:pb-0"><ClientPayment /></div></>} />
                        <Route path="/my-bookings/community/:tourId" element={<><ClientNav /><TripCommunity /></>} />
                        <Route path="/my-documents" element={<><ClientNav /><div className="pt-0 sm:pt-14 pb-24 sm:pb-0"><MyDocuments /></div></>} />
                        <Route path="/depoimentos" element={<><ClientNav /><div className="pt-0 sm:pt-14 pb-24 sm:pb-0"><ClientTestimonials /></div></>} />
                        <Route path="/testimonials" element={<><ClientNav /><div className="pt-0 sm:pt-14 pb-24 sm:pb-0"><ClientTestimonials /></div></>} />
                        <Route path="/recursos" element={<><ClientNav /><div className="pt-0 sm:pt-14 pb-24 sm:pb-0"><SpiritualLife /></div></>} />
                        <Route path="/recursos/biblia" element={<><ClientNav /><div className="pt-0 sm:pt-14 pb-24 sm:pb-0"><Bible /></div></>} />
                        <Route path="/recursos/leituras" element={<><ClientNav /><div className="pt-0 sm:pt-14 pb-24 sm:pb-0"><DailyReadings /></div></>} />
                        <Route path="/recursos/rosario" element={<><ClientNav /><div className="pt-0 sm:pt-14 pb-24 sm:pb-0"><Rosary /></div></>} />
                        <Route path="/recursos/clima" element={<><ClientNav /><div className="pt-0 sm:pt-14 pb-24 sm:pb-0"><Weather /></div></>} />
                        <Route path="/recursos/moedas" element={<><ClientNav /><div className="pt-0 sm:pt-14 pb-24 sm:pb-0"><CurrencyConverter /></div></>} />
                        <Route path="/recursos/fusos" element={<><ClientNav /><div className="pt-0 sm:pt-14 pb-24 sm:pb-0"><TimeZones /></div></>} />
                        <Route path="/recursos/dicas" element={<><ClientNav /><div className="pt-0 sm:pt-14 pb-24 sm:pb-0"><TravelTips /></div></>} />
                        <Route path="/recursos/jogos" element={<><ClientNav /><div className="pt-0 sm:pt-14 pb-24 sm:pb-0"><GamesHub /></div></>} />
                        <Route path="/about" element={<Home />} />
                        <Route path="/faq" element={<Home />} />
                        <Route path="/faqs" element={<FAQs />} />
                        <Route path="/contact" element={<Home />} />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </main>
                    <Footer />
                  </>
                } />
              </Routes>
              </Suspense>
              <Toaster />
              <PWAInstallPrompt />
              <ForcePasswordChangeModal />
            </div>
          </BrowserRouter>
          </SiteLayoutProvider>
          </HeroMusicProvider>
        </LanguageProvider>
        </ClientPreferencesProvider>
      </ClientAuthProvider>
    </AuthProvider>
  );
}

export default App;
